<template>
  <div id="app">
    <EcNavbar v-if="showNavbar" />
    <SideCart :is-visible="isSideCartVisible" @close="closeSideCart" />

    <main role="main" class="pd-main-content">
      <router-view />
    </main>

    <footer class="pd-footer" v-if="showFooter">
      <div class="pd-container">
        <div class="pd-footer-main">
          <!-- Logo & Brand -->
          <div class="pd-footer-brand">
            <img src="/pd-logo-white.png" alt="Point of Demon" class="pd-footer-logo" />
            <p class="pd-footer-slogan">釋放你內心的惡魔</p>
            <div class="pd-footer-social">
              <a href="https://www.instagram.com/pointofdemon?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i class="fab fa-instagram"></i></a>
            </div>
          </div>

          <!-- Quick Links -->
          <div class="pd-footer-links">
            <h4>關於我們</h4>
            <ul>
              <li><router-link to="/about">品牌故事</router-link></li>
              <li><router-link to="/products">全部商品</router-link></li>
            </ul>
          </div>

          <!-- Customer Service -->
          <div class="pd-footer-links">
            <h4>顧客服務</h4>
            <ul>
              <li><router-link to="/faq">常見問題 FAQ</router-link></li>
              <li><router-link to="/return-policy">退換貨政策</router-link></li>
              <li><router-link to="/shipping-info">配送說明</router-link></li>
              <li><router-link to="/size-guide">尺寸指南</router-link></li>
            </ul>
          </div>

          <!-- Contact -->
          <div class="pd-footer-links">
            <h4>聯絡我們</h4>
            <ul>
              <li><a href="mailto:pointofdemon@gmail.com">pointofdemon@gmail.com</a></li>
            </ul>
          </div>
        </div>

        <!-- Bottom Bar -->
        <div class="pd-footer-bottom">
          <p>© 2026 Point of Demon. All rights reserved.</p>
          <div class="pd-footer-legal">
            <router-link to="/privacy-policy">隱私權政策</router-link>
            <span>|</span>
            <router-link to="/terms-of-service">服務條款</router-link>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import EcNavbar from '@/components/EcNavbar'
import SideCart from '@/components/SideCart'
import { supabase } from './supabase'
import store from './store'

export default {
  components: {
    EcNavbar,
    SideCart
  },
  computed: {
    showNavbar () {
      return this.$route.name !== 'orderCreate' && !this.$route.path.startsWith('/admin')
    },
    showFooter () {
      // Hide footer on admin and auth pages
      const authPages = ['/signin', '/signup', '/forgot-password', '/reset-password', '/auth/callback']
      return !this.$route.path.startsWith('/admin') && !authPages.includes(this.$route.path)
    }
  },
  data () {
    return {
      isSideCartVisible: false
    }
  },
  created () {
    // Auth listener moved here to bypass .js file update issues
    supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth event in App.vue:', event)
      console.log('Current route:', this.$route.path)
      console.log('Current hash:', window.location.hash)

      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        store.dispatch('fetchCurrentUser')

        // If user just signed in via OAuth (detected by access_token in URL)
        // or is on signin/signup page, redirect to home
        const isOAuthCallback = window.location.hash.includes('access_token')
        const isAuthPage = this.$route.path === '/signin' || this.$route.path === '/signup'

        if (event === 'SIGNED_IN' && (isOAuthCallback || isAuthPage)) {
          console.log('OAuth 登入成功，重新導向到首頁')

          // Clean up the URL hash
          if (isOAuthCallback) {
            window.location.hash = ''
          }

          // Only redirect if not already on home page
          if (this.$route.path !== '/home') {
            this.$router.push('/home').catch(err => {
              // Ignore navigation duplicated error
              if (err.name !== 'NavigationDuplicated') {
                console.error('Navigation error:', err)
              }
            })
          }
        }
      } else if (event === 'SIGNED_OUT') {
        store.commit('logout')
      }
    })

    this.$root.$on('toggleSideCart', this.toggleSideCart)
  },
  beforeDestroy () {
    this.$root.$off('toggleSideCart', this.toggleSideCart)
  },
  methods: {
    toggleSideCart () {
      this.isSideCartVisible = !this.isSideCartVisible
    },
    closeSideCart () {
      this.isSideCartVisible = false
    }
  }
}
</script>

<style>
:root {
  --navbar-height: 8rem; /* 128px, provides global top padding for pages */
}

/* Global Styles */
#app {
  font-family: 'Noto Sans TC', sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.pd-main-content {
  flex: 1;
}

/* Footer */
.pd-footer {
  background-color: var(--pd-black, #000);
  color: var(--pd-white, #fff);
  padding: 4rem 0 0;
  margin-top: 4rem;
}

.pd-footer-main {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 3rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid #333;
}

/* Brand Section */
.pd-footer-brand {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.pd-footer-logo {
  height: 80px;
  width: auto;
  object-fit: contain;
  align-self: flex-start;
}

.pd-footer-slogan {
  font-size: 0.875rem;
  color: #888;
  margin: 0;
  letter-spacing: 0.1em;
}

.pd-footer-social {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.pd-footer-social a {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #444;
  color: #888;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.pd-footer-social a:hover {
  border-color: var(--pd-white, #fff);
  color: var(--pd-white, #fff);
}

/* Links Sections */
.pd-footer-links h4 {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin: 0 0 1.5rem;
  color: var(--pd-white, #fff);
}

.pd-footer-links ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.pd-footer-links li {
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #888;
}

.pd-footer-links a {
  color: #888;
  text-decoration: none;
  transition: color 0.2s ease;
}

.pd-footer-links a:hover {
  color: var(--pd-white, #fff);
}

/* Bottom Bar */
.pd-footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 0;
}

.pd-footer-bottom p {
  margin: 0;
  font-size: 0.75rem;
  color: #666;
}

.pd-footer-legal {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.75rem;
}

.pd-footer-legal a {
  color: #666;
  text-decoration: none;
  transition: color 0.2s ease;
}

.pd-footer-legal a:hover {
  color: var(--pd-white, #fff);
}

.pd-footer-legal span {
  color: #444;
}

@media (max-width: 1024px) {
  .pd-footer-main {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
  }

  .pd-footer-brand {
    grid-column: span 2;
    align-items: center;
    text-align: center;
  }

  .pd-footer-logo {
    align-self: center;
  }

  .pd-footer-social {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .pd-footer {
    padding: 3rem 0 0;
  }

  .pd-footer-main {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }

  .pd-footer-brand {
    grid-column: span 1;
  }

  .pd-footer-logo {
    height: 70px;
  }

  .pd-footer-links h4 {
    margin-bottom: 1rem;
  }

  .pd-footer-links li {
    margin-bottom: 0.5rem;
  }

  .pd-footer-bottom {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}
</style>
